import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Checkbox,
	useTheme,
	useMediaQuery,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	RadioGroup,
	FormControlLabel,
	Radio,
	Alert
} from '@mui/material';
import axios from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import i18next, {getBrowserLanguage} from '../../lang';
import ConfirmDialog from "../../components/ConfirmDialog";
import OrderUploadDialog, {
	DuplicateClassroomOrderData,
	DuplicateProductOrderData,
	InvalidOrderData, OrderData
} from "../../components/OrderUploadDialog";
import { rootStore } from "../../stores/RootStore";
import PurchaseStepper from "../../components/PurchaseStepper";
import { Download as DownloadIcon, Upload as UploadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
	DeliveryAddress,
	ExcelOrder as Order,
	DuplicateAliasRow,
	ConflictWarning
} from './interfaces';

interface OrderUploadResponse {
	normalOrders: OrderData[];
	invalidOrders: InvalidOrderData[];
	duplicateClassroomOrders: DuplicateClassroomOrderData[];
	duplicateProductOrders: DuplicateProductOrderData[];
}

interface LocationState {
	selectedAddressIds?: number[];
	prevOrderData?: {
		deliveryData: any[];
		orderQuantities: any[];
		products: any[];
		productSets: any[];
		categories: any[];
	};
	fromOrderConfirm?: boolean;
	lockedAddressIds?: number[];
	hasTempOrder?: boolean;
}

const DeliveryAddressList: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { viewStatusStore, authStore } = rootStore;
	const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
	const [isExcelNotFoundDialogOpen, setIsExcelNotFoundDialogOpen] = useState(false);
	const [excelNotFoundIds, setExcelNotFoundIds] = useState<string[]>([]);
	const [isOrderUploadDialogOpen, setIsOrderUploadDialogOpen] = useState(false);
	const [orderUploadData, setOrderUploadData] = useState<OrderUploadResponse | null>(null);
	const { selectedAddressIds, prevOrderData, fromOrderConfirm, lockedAddressIds: initialLockedIds, hasTempOrder: hasTempOrderFromState } = (location.state as LocationState) || {};
	const [tempOrder, setTempOrder] = useState<any>(null);
	const [isTempOrderDialogOpen, setIsTempOrderDialogOpen] = useState(false);
	const [deliveryAddresses, setDeliveryAddresses] = useState<DeliveryAddress[]>([]);
	const [filteredAddresses, setFilteredAddresses] = useState<DeliveryAddress[]>([]);
	const [searchParams, setSearchParams] = useState({
		keyword: '',
	});
	const [selectedItems, setSelectedItems] = useState<DeliveryAddress[]>([]);
	const [lockedAddressIds, setLockedAddressIds] = useState<number[]>([]);
	const [duplicateAliasRows, setDuplicateAliasRows] = useState<DuplicateAliasRow[]>([]);
	const [isDuplicateAliasDialogOpen, setIsDuplicateAliasDialogOpen] = useState(false);
	const [conflictWarnings, setConflictWarnings] = useState<ConflictWarning[]>([]);
	const [isConflictWarningDialogOpen, setIsConflictWarningDialogOpen] = useState(false);

	useEffect(() => {
		const skipDialog = !!(location.state as any)?.skipTempOrderDialog;
		const checkTempOrder = async () => {
			let tempOrderData = location.state?.tempOrder;
			if (!tempOrderData) {
				try {
					const response = await axios.get('/api/v1/orders/temp');
					if (response.data) {
						tempOrderData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
					}
				} catch (error) {
					// 임시 저장 없음
				}
			} else if (typeof tempOrderData === 'string') {
				try { tempOrderData = JSON.parse(tempOrderData); } catch { /* 이미 객체 */ }
			}
			if (tempOrderData && typeof tempOrderData === 'object' && Object.keys(tempOrderData).length > 0) {
				setTempOrder(tempOrderData);
				if (!skipDialog) {
					setIsTempOrderDialogOpen(true);
				}
				window.history.replaceState({}, document.title);
			}
		};
		checkTempOrder();
	}, []);

	useEffect(() => {
		if (selectedAddressIds && deliveryAddresses.length > 0) {
			const selectedAddresses = deliveryAddresses.filter(address =>
				selectedAddressIds.includes(address.addressId)
			);
			setSelectedItems(selectedAddresses);

			if (fromOrderConfirm) {
				setLockedAddressIds(selectedAddressIds);
			} else if (initialLockedIds && initialLockedIds.length > 0) {
				setLockedAddressIds(initialLockedIds);
			} else {
				setLockedAddressIds([]);
			}
		}
	}, [selectedAddressIds, deliveryAddresses, fromOrderConfirm, initialLockedIds]);

	const fetchDeliveryAddresses = useCallback(async () => {
		try {
			const response = await axios.get('/api/v1/orders/addresses');

			setDeliveryAddresses(response.data);
			setFilteredAddresses(response.data);
		} catch (error: any) {
			console.error('Failed to fetch delivery addresses:', error);
		}
	}, []);

	useEffect(() => {
		fetchDeliveryAddresses();
	}, [fetchDeliveryAddresses]);

	const handleSearch = () => {
		if (!searchParams.keyword.trim()) {
			setFilteredAddresses(deliveryAddresses);
			return;
		}

		const filtered = deliveryAddresses.filter(address =>
			address.classroomName.toLowerCase().includes(searchParams.keyword.toLowerCase()) ||
			(address.aliasName || '').toLowerCase().includes(searchParams.keyword.toLowerCase()) ||
			address.address.toLowerCase().includes(searchParams.keyword.toLowerCase()) ||
			address.phone.toLowerCase().includes(searchParams.keyword.toLowerCase()) ||
			address.userName.toLowerCase().includes(searchParams.keyword.toLowerCase())
		);
		setFilteredAddresses(filtered);
	};

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			if (selectedItems.length === 0) {
				const selectableAddresses = filteredAddresses.filter(address => !lockedAddressIds.includes(address.addressId));
				setSelectedItems([...selectedItems, ...selectableAddresses]);
			} else {
				const noneSelectedItems = filteredAddresses.filter(fa =>
					!selectedItems.some(si => si.addressId === fa.addressId) && !lockedAddressIds.includes(fa.addressId)
				);
				const newSelectedItems = [...selectedItems, ...noneSelectedItems];
				setSelectedItems(newSelectedItems);
			}
		} else {
			setSelectedItems((si) => si.filter((item) => lockedAddressIds.includes(item.addressId)));
		}
	};

	const handleCheckboxChange = (address: DeliveryAddress) => {
		if (lockedAddressIds.includes(address.addressId)) {
			return;
		}

		const selectedIndex = selectedItems.findIndex(item => item.addressId === address.addressId);
		let newSelectedItems: DeliveryAddress[] = [];

		if (selectedIndex === -1) {
			newSelectedItems = [...selectedItems, address];
		} else {
			newSelectedItems = selectedItems.filter(item => item.addressId !== address.addressId);
		}

		setSelectedItems(newSelectedItems);
	};

	const handleDeleteTempOrder = async () => {
		try {
			await axios.delete(`/api/v1/orders/temp`);
		} catch (error) {
			console.error('Failed to delete temp order:', error);
		}
		setTempOrder(null);
		setIsTempOrderDialogOpen(false);
	}

	const handleDismissTempOrderDialog = () => {
		setIsTempOrderDialogOpen(false);
	}

	const handleContinueTempOrder = () => {
		navigate('/purchase/order-input', { state: { tempOrder } });
	}

	// この画面自体に tempOrder があるか、前の画面から引き継いだフラグのOR
	const hasTempOrder = !!tempOrder || !!hasTempOrderFromState;

	const handleCancelOrder = (tempOrderDeleted = false) => {
		const effectiveHasTempOrder = tempOrderDeleted ? false : hasTempOrder;
		setIsCancelOrderDialogOpen(false);
		if (fromOrderConfirm && prevOrderData) {
			navigate('/purchase/order-confirm', {
				state: {
					orderData: {
						deliveryData: prevOrderData.deliveryData,
						orderQuantities: prevOrderData.orderQuantities
					},
					products: prevOrderData.products,
					productSets: prevOrderData.productSets,
					categories: prevOrderData.categories,
					userProductMappings: (prevOrderData as any).userProductMappings,
					hasTempOrder: effectiveHasTempOrder
				}
			});
		} else if (authStore.user.level === 'CLASSROOM_MANAGER') {
			navigate('/purchase/delivery-datetime', { state: { hasTempOrder: effectiveHasTempOrder } });
		} else {
			navigate('/purchase/delivery-address', { state: { hasTempOrder: effectiveHasTempOrder } });
		}
	};

	const handleCancelOrderWithDeleteTemp = async () => {
		try {
			await axios.delete('/api/v1/orders/temp');
		} catch (error) {
			console.error('Failed to delete temp order:', error);
		}
		handleCancelOrder(true);
	};

	const handleDownloadDeliveryAddresses = () => {
		if (deliveryAddresses.length === 0) {
			return;
		}

		// 엑셀 데이터 준비
		const excelData = deliveryAddresses.map((address, index) => ({
			[i18next.t('common.id')]: address.id,
			[i18next.t('order.classroom')]: address.classroomName,
			[i18next.t('address.alias')]: address.aliasName || '',
			[i18next.t('address.address')]: address.address,
			[i18next.t('contact.phone')]: address.phone,
			[i18next.t('purchase.person_in_charge')]: address.userName
		}));

		// 워크북 생성
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.json_to_sheet(excelData);

		// 열 너비 설정
		const columnWidths = [
			{ wch: 20 },
			{ wch: 70 },
			{ wch: 20 },
			{ wch: 70 },
			{ wch: 20 },
			{ wch: 20 },
		];
		worksheet['!cols'] = columnWidths;

		// 워크시트를 워크북에 추가
		XLSX.utils.book_append_sheet(workbook, worksheet, i18next.t('address.delivery_address'));

		// 파일 다운로드
		const fileName = `${i18next.t('address.delivery_address')}_${new Date().toISOString().split('T')[0]}.xlsx`;
		XLSX.writeFile(workbook, fileName);
	};

	const handleUploadDeliveryAddresses = () => {
		setExcelNotFoundIds([]);
		setConflictWarnings([]);
		setDuplicateAliasRows([]);
		// 파일 입력 요소 생성
		const fileInput = document.createElement('input');
		fileInput.type = 'file';
		fileInput.accept = '.xlsx';
		fileInput.style.display = 'none';

		fileInput.onchange = async (event) => {
			const target = event.target as HTMLInputElement;
			const file = target.files?.[0];

			if (!file) return;

			try {
				const arrayBuffer = await file.arrayBuffer();
				const workbook = XLSX.read(arrayBuffer, { type: 'array' });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

				const newSelectedItems: DeliveryAddress[] = [];
				const notFoundIds: string[] = [];
				const newDuplicateAliasRows: DuplicateAliasRow[] = [];
				const newConflictWarnings: ConflictWarning[] = [];

				// Excel 컬럼: A(ID) | B(교실) | C(별칭) | D(주소) | E(전화) | F(담당자)
				for (let row = 1; row <= range.e.r; row++) {
					const getCell = (col: number) => worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
					const id = getCell(0)?.v?.toString().trim() || '';
					const alias = getCell(2)?.v?.toString().trim() || '';
					const address = getCell(3)?.v?.toString().trim() || '';

					// 4. 셋 다 없음 → 빈 행 무시
					if (!id && !alias && !address) continue;

					let matched: DeliveryAddress | null = null;

					// 1. ID 매칭
					if (id) {
						const foundAddress = deliveryAddresses.find(addr => String(addr.id) === id);
						if (foundAddress) {
							matched = foundAddress;
							// ID와 alias/address 일치 여부 확인
							if (alias && foundAddress.aliasName && alias !== foundAddress.aliasName) {
								newConflictWarnings.push({
									excelRow: row + 1,
									id,
									field: i18next.t('address.alias'),
									excelValue: alias,
									actualValue: foundAddress.aliasName
								});
							}
							if (address && address !== foundAddress.address) {
								newConflictWarnings.push({
									excelRow: row + 1,
									id,
									field: i18next.t('address.address'),
									excelValue: address,
									actualValue: foundAddress.address
								});
							}
						}
						// ID 매칭 실패 → alias 시도
					}

					// 2. alias 매칭 (ID 매칭 실패 시)
					if (!matched && alias) {
						const aliasMatches = deliveryAddresses.filter(addr => addr.aliasName === alias);
						if (aliasMatches.length === 1) {
							matched = aliasMatches[0];
						} else if (aliasMatches.length > 1) {
							newDuplicateAliasRows.push({
								excelRow: row + 1,
								aliasName: alias,
								candidates: aliasMatches,
								selectedId: null
							});
							continue;
						}
						// 0건 → address 시도
					}

					// 3. address 매칭 (ID, alias 모두 실패 시)
					if (!matched && address) {
						const addressMatches = deliveryAddresses.filter(addr => addr.address === address);
						if (addressMatches.length === 1) {
							matched = addressMatches[0];
						} else {
							notFoundIds.push(id || alias || address);
							continue;
						}
					}

					// ID가 있었지만 매칭 실패 (alias/address도 없는 경우)
					if (!matched && id) {
						notFoundIds.push(id);
						continue;
					}

					if (matched) {
						if (!selectedItems.some(item => item.addressId === matched!.addressId) &&
							!newSelectedItems.some(item => item.addressId === matched!.addressId) &&
							!lockedAddressIds.includes(matched.addressId)) {
							newSelectedItems.push(matched);
						}
					}
				}

				// 새로운 항목들을 selectedItems에 추가
				if (newSelectedItems.length > 0) {
					setSelectedItems(prev => [...prev, ...newSelectedItems]);
				}

				// 충돌 경고 표시
				if (newConflictWarnings.length > 0) {
					setConflictWarnings(newConflictWarnings);
					setIsConflictWarningDialogOpen(true);
				}

				// 중복 alias 선택 다이얼로그 표시
				if (newDuplicateAliasRows.length > 0) {
					setDuplicateAliasRows(newDuplicateAliasRows);
					setIsDuplicateAliasDialogOpen(true);
				}

				// 존재하지 않는 ID/alias들
				if (notFoundIds.length > 0) {
					setExcelNotFoundIds(notFoundIds);
					setIsExcelNotFoundDialogOpen(true);
				}

				document.body.removeChild(fileInput);

			} catch (error) {
				console.error('엑셀 파일 읽기 오류:', error);
				document.body.removeChild(fileInput);
			}
		};

		document.body.appendChild(fileInput);
		fileInput.click();
	}

	const handleDuplicateAliasConfirm = () => {
		const newSelectedItems: DeliveryAddress[] = [];
		for (const row of duplicateAliasRows) {
			if (row.selectedId !== null) {
				const selected = row.candidates.find(c => c.addressId === row.selectedId);
				if (selected && !selectedItems.some(item => item.addressId === selected.addressId) && !lockedAddressIds.includes(selected.addressId)) {
					newSelectedItems.push(selected);
				}
			}
		}
		if (newSelectedItems.length > 0) {
			setSelectedItems(prev => [...prev, ...newSelectedItems]);
		}
		setIsDuplicateAliasDialogOpen(false);
		setDuplicateAliasRows([]);
	}

	const handleDownloadTemplate = () => {
		// ヘッダー: 教室 | 宛名 | *教材名 | 巻 | 教科コード | *数量 | 配送日 | 時間帯
		const headers = [
			i18next.t('order.classroom'),
			i18next.t('address.alias_label'),
			'*' + i18next.t('order.textbook_name'),
			i18next.t('product.volume'),
			i18next.t('product.subject_code'),
			'*' + i18next.t('order.quantity'),
			i18next.t('order.delivery_date'),
			i18next.t('order.time_slot')
		];

		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet([headers]);

		worksheet['!cols'] = [
			{ wch: 20 }, // 教室
			{ wch: 20 }, // 宛名
			{ wch: 30 }, // *教材名
			{ wch: 10 }, // 巻
			{ wch: 15 }, // 教科コード
			{ wch: 10 }, // *数量
			{ wch: 15 }, // 配送日
			{ wch: 15 }, // 時間帯
		];

		XLSX.utils.book_append_sheet(workbook, worksheet, i18next.t('purchase.order_list_title'));
		const fileName = `order_upload_template_${new Date().toISOString().split('T')[0]}.xlsx`;
		XLSX.writeFile(workbook, fileName);
	};

	const handleOrderUploadConfirm = (invalidData: Order[], duplicateData: Order[]) => {
		// 수정된 데이터를 다시 백엔드로 전송
		const updatedData = [...invalidData, ...duplicateData];
		if (updatedData.length > 0) {
			// 여기서 수정된 데이터를 다시 처리하는 로직을 추가할 수 있습니다
			console.log("수정된 주문 데이터:", updatedData);
		}
		setIsOrderUploadDialogOpen(false);
		setOrderUploadData(null);
	};

	const handleOrderUploadCancel = () => {
		setIsOrderUploadDialogOpen(false);
		setOrderUploadData(null);
	};

	const handleUploadOrders = () => {
		// 파일 입력 요소 생성
		const fileInput = document.createElement('input');
		fileInput.type = 'file';
		fileInput.accept = '.xlsx';
		fileInput.style.display = 'none';

		fileInput.onchange = async (event) => {
			const target = event.target as HTMLInputElement;
			const file = target.files?.[0];

			if (!file) return;

			try {
				// 파일을 ArrayBuffer로 읽기
				const arrayBuffer = await file.arrayBuffer();
				const workbook = XLSX.read(arrayBuffer, { type: 'array' });

				// 첫 번째 시트 가져오기
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];

				// A열 2행부터 데이터 읽기 (A2부터)
				const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
				const newSelectedItems: Order[] = [];

				// A열 2행부터 읽기
				// A(0): classroomName, B(1): aliasName, C(2): productTitle, D(3): productVolume,
				// E(4): subjectCode, F(5): quantity, G(6): deliveryDate, H(7): timeSlot
				for (let row = 1; row <= range.e.r; row++) {
					const getCell = (col: number) => worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
					const classroomNameCell = getCell(0);
					const aliasNameCell = getCell(1);
					const productTitleCell = getCell(2);
					const productVolumeCell = getCell(3);
					const subjectCodeCell = getCell(4);
					const productQuantityCell = getCell(5);
					const deliveryDateCell = getCell(6);
					const timeSlotCell = getCell(7);

					const classroomName = classroomNameCell?.v?.toString().trim() || null;
					const aliasName = aliasNameCell?.v?.toString().trim() || null;
					const productTitle = productTitleCell?.v?.toString().trim() || null;
					const productVolume = productVolumeCell?.v?.toString().trim() || null;
					const subjectCode = subjectCodeCell?.v?.toString().trim() || null;
					const quantity = parseInt(productQuantityCell?.v?.toString().trim() || null);
					const deliveryDate = XLSX.SSF.format("yyyy-mm-dd", deliveryDateCell?.v) || null;
					const timeSlot = timeSlotCell?.v?.toString().trim() || null;

					newSelectedItems.push({
						line: row + 1,
						classroomName,
						aliasName,
						productTitle,
						productVolume,
						subjectCode,
						quantity,
						deliveryDate,
						timeSlot
					});
				}

				if (newSelectedItems.length > 0) {
					try {
						viewStatusStore.setLoadingStatus({ open: true });
						const response = await axios.post('/api/v1/orders/excel', newSelectedItems);

						if (response.data && (response.data.invalidOrders?.length > 0 || response.data.duplicateClassroomOrders?.length > 0 || response.data.duplicateProductOrders?.length > 0)) {
							setOrderUploadData(response.data);
							setIsOrderUploadDialogOpen(true);
						} else {

						}
					} catch (error) {
						console.error('주문 데이터 업로드 오류:', error);
					} finally {
						viewStatusStore.setLoadingStatus({ open: false });
					}
				}

				// 파일 입력 요소 제거
				document.body.removeChild(fileInput);

			} catch (error) {
				console.error('엑셀 파일 읽기 오류:', error);
				document.body.removeChild(fileInput);
			}
		};

		// 파일 입력 요소를 DOM에 추가하고 클릭
		document.body.appendChild(fileInput);
		fileInput.click();
	}

	return (
		<Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 3 }, width: '100%' }}>
			<PurchaseStepper activeStep={0} />
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Box sx={{ mb: 3 }}>
						<Typography variant="h6" gutterBottom>
							{i18next.t('purchase.select_delivery_address')}
						</Typography>
						{isMobile ? (
							<Stack spacing={1} sx={{ mb: 2 }}>
								{tempOrder && !isTempOrderDialogOpen && (
									<Button
										variant="outlined"
										color="warning"
										sx={{ fontSize: '0.8rem', height: '35px' }}
										onClick={handleContinueTempOrder}
									>
										{i18next.t('purchase.go_to_temp_order')}
									</Button>
								)}
								<Button
									variant="contained"
									startIcon={<DownloadIcon />}
									sx={{
										fontSize: '0.9rem',
										height: '35px',
										backgroundColor: 'primary.main',
										boxShadow: 1,
										'&:hover': { backgroundColor: 'primary.dark' }
									}}
									onClick={handleDownloadDeliveryAddresses}
								>
									{i18next.t('address.delivery_address')}
								</Button>
								<TextField
									fullWidth
									label={i18next.t('common.keyword')}
									placeholder={i18next.t('order.search_hint')}
									size="small"
									sx={{
										'& .MuiInputBase-root': {
											height: '35px'
										},
										'& .MuiInputLabel-root': {
											fontSize: '0.8rem'
										},
										'& .MuiOutlinedInput-root': {
											fontSize: '0.8rem'
										}
									}}
									value={searchParams.keyword}
									onChange={(e) => setSearchParams({ ...searchParams, keyword: e.target.value })}
									onKeyPress={(e) => {
										if (e.key === 'Enter') {
											handleSearch();
										}
									}}
								/>
								<Button
									variant="outlined"
									fullWidth
									onClick={handleSearch}
									sx={{
										fontSize: '0.8rem',
										fontWeight: 'bold',
										height: '35px',
										minWidth: '80px'
									}}
								>
									{i18next.t('common.search')}
								</Button>
							</Stack>
						) : (
							<Grid container spacing={3} sx={{ mb: 2 }}>
								<Grid item xs={12}>
									<Grid container spacing={1} alignItems="center" justifyContent="flex-end" sx={{ height: '100%' }}>
										{tempOrder && !isTempOrderDialogOpen && (
											<Grid item>
												<Button
													variant="outlined"
													color="warning"
													sx={{
														fontSize: '0.85rem',
														height: '35px',
														mr: 1,
													}}
													onClick={handleContinueTempOrder}
												>
													{i18next.t('purchase.go_to_temp_order')}
												</Button>
											</Grid>
										)}
										<Grid item>
											<Button
												variant="contained"
												startIcon={<DownloadIcon />}
												sx={{
													fontSize: '0.9rem',
													height: '35px',
													minWidth: '110px',
													backgroundColor: 'primary.main',
													mr: 2,
													boxShadow: 1,
													'&:hover': { backgroundColor: 'primary.dark' }
												}}
												onClick={handleDownloadDeliveryAddresses}
											>
												{i18next.t('address.delivery_address')}
											</Button>
										</Grid>
										<Grid item sm={3}>
											<TextField
												fullWidth
												label={i18next.t('common.keyword')}
												placeholder={i18next.t('order.search_hint')}
												size="small"
												sx={{
													'& .MuiInputBase-root': {
														height: '35px'
													},
													'& .MuiInputLabel-root': {
														fontSize: '0.8rem'
													},
													'& .MuiOutlinedInput-root': {
														fontSize: '0.8rem'
													}
												}}
												value={searchParams.keyword}
												onChange={(e) => setSearchParams({ ...searchParams, keyword: e.target.value })}
												onKeyPress={(e) => {
													if (e.key === 'Enter') {
														handleSearch();
													}
												}}
											/>
										</Grid>
										<Grid item>
											<Button
												variant="outlined"
												onClick={handleSearch}
												sx={{
													fontSize: '0.8rem',
													fontWeight: 'bold',
													height: '35px',
													minWidth: '80px'
												}}
											>
												{i18next.t('common.search')}
											</Button>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						)}
					</Box>

					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						mt: '20px'
					}}>
						<Paper sx={{
							display: 'flex',
							flexDirection: 'column',
							boxShadow: 'none',
							border: 'none',
							borderRadius: '4px',
							backgroundColor: '#fff',
						}}>
							<TableContainer>
								<Table sx={{
									minWidth: 650,
									'& .MuiTableCell-root': {
										borderRight: '1px solid #f0f0f0',
										borderBottom: '1px solid #f0f0f0',
										padding: '0 8px',
										height: '40px'
									}
								}} size="small">
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" sx={{
												backgroundColor: '#f5f5f5',
												width: '3% !important',
												height: '40px !important'
											}}>
												<Checkbox
													size="small"
													indeterminate={selectedItems.length > 0 && selectedItems.length < filteredAddresses.length}
													checked={filteredAddresses.length > 0 && filteredAddresses.every(fa => selectedItems.some(si => si.addressId === fa.addressId))}
													onChange={handleSelectAllClick}
													sx={{
														color: '#bdbdbd',
														'&.Mui-checked': {
															color: '#2196f3'
														},
														padding: '0 0 0 10px'
													}}
												/>
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '3%',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('common.number')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '200px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('order.classroom')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('address.alias')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '300px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('address.address')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('contact.phone')}
											</TableCell>
											<TableCell sx={{
												backgroundColor: '#f5f5f5',
												fontSize: '0.7rem',
												fontWeight: 800,
												width: '130px',
												textAlign: 'center',
												height: '40px !important'
											}}>
												{i18next.t('purchase.person_in_charge')}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{filteredAddresses.map((address, index) => (
											<TableRow
												key={address.addressId}
												sx={{
													height: '40px !important',
													backgroundColor: lockedAddressIds.includes(address.addressId)
														? '#f5f5f5'
														: selectedItems.some(item => item.addressId === address.addressId)
															? '#FFFFD0'
															: 'inherit',
													'&:hover': {
														backgroundColor: lockedAddressIds.includes(address.addressId)
															? '#f5f5f5'
															: selectedItems.some(item => item.addressId === address.addressId)
																? '#FFFFD0'
																: '#f5f5f5'
													}
												}}
											>
												<TableCell padding="checkbox" sx={{ height: '40px !important' }}>
													<Checkbox
														size="small"
														checked={selectedItems.some(item => item.addressId === address.addressId)}
														onChange={() => handleCheckboxChange(address)}
														disabled={lockedAddressIds.includes(address.addressId)}
														sx={{
															color: '#bdbdbd',
															'&.Mui-checked': {
																color: lockedAddressIds.includes(address.addressId) ? '#9e9e9e' : '#2196f3'
															},
															padding: '0 0 0 10px'
														}}
													/>
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{index + 1}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.classroomName}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.aliasName || ''}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', height: '40px !important' }}>
													{address.address}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.phone}
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', height: '40px !important' }}>
													{address.userName}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
								<Typography sx={{ fontSize: '0.7rem', color: '#666', ml: 1 }}>
									TOTAL
								</Typography>
								<Typography sx={{ fontSize: '0.7rem', color: '#666', mr: 1 }}>
									{selectedItems.length} / {deliveryAddresses.length}
								</Typography>
							</Box>
							<Box sx={{
								p: 1,
								backgroundColor: '#fff',
								borderTop: '1px solid #e0e0e0',
								display: 'flex',
								flexDirection: { xs: 'column', md: 'row' },
								justifyContent: 'flex-end',
								alignItems: { xs: 'stretch', md: 'center' },
								gap: 1,
								minHeight: '35px',
								flexShrink: 0
							}}>
								<Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
									<Button
										variant="outlined"
										sx={{
											fontSize: '0.8rem',
											height: '28px',
											minWidth: '80px',
											mr: { xs: 0, md: 1 },
											width: { xs: '100%', md: 'auto' }
										}}
										onClick={() => setIsCancelOrderDialogOpen(true)}
									>
										{fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
									</Button>
								</Box>
								<Box style={{marginTop: 20}}>
									<Button
										variant="contained"
										disabled={lockedAddressIds.length === 0 ? selectedItems.length === 0 : lockedAddressIds.length === selectedItems.length}
										sx={{
											fontSize: '0.8rem',
											height: '28px',
											minWidth: '80px',
											mr: { xs: 0, md: 1 },
											backgroundColor: 'primary.main',
											margin: 0,
											width: { xs: '100%', md: 'auto' },
											'&:hover': {
												backgroundColor: 'primary.dark'
											}
										}}
										onClick={() => {
											const newlySelectedAddresses = selectedItems.filter(item =>
												!lockedAddressIds.includes(item.addressId)
											);

											navigate('/purchase/delivery-datetime', {
												state: {
													selectedAddresses: newlySelectedAddresses,
													prevDeliveryData: prevOrderData?.deliveryData?.reduce((acc, delivery) => ({
														...acc,
														[delivery.addressId]: {
															date: delivery.deliveryDate,
															time: delivery.deliveryTime
														}
													}), {}),
													prevOrderData,
													lockedAddressIds,
											fromOrderConfirm,
													hasTempOrder
												}
											});
										}}
									>
										{i18next.t('purchase.go_to_delivery_date')}
									</Button>
								</Box>
							</Box>
						</Paper>
					</Box>
				</Grid>
			</Grid>
			<Dialog
				open={isTempOrderDialogOpen}
				onClose={handleDismissTempOrderDialog}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
					{i18next.t('purchase.temp_order_found_title')}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
						{i18next.t('purchase.temp_order_found_message')}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button onClick={handleDeleteTempOrder} color="error" variant="outlined" size="small">
						{i18next.t('purchase.delete_temp_order')}
					</Button>
					<Box sx={{ flexGrow: 1 }} />
					<Button onClick={handleDismissTempOrderDialog} variant="outlined" size="small">
						{i18next.t('common.cancel')}
					</Button>
					<Button onClick={handleContinueTempOrder} variant="contained" size="small">
						{i18next.t('purchase.continue_temp_order')}
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmDialog
				open={isCancelOrderDialogOpen}
				onClose={() => setIsCancelOrderDialogOpen(false)}
				onConfirm={handleCancelOrder}
				title={fromOrderConfirm ? i18next.t('purchase.cancel_and_return_to_order_confirm') : i18next.t('order.order_cancelled')}
				message={fromOrderConfirm ? i18next.t('purchase.confirm_cancel_add_delivery') : i18next.t('common.confirm_cancel')}
				button1Text={i18next.t('common.no')}
				button2Text={i18next.t('common.yes')}
				onConfirmAlt={hasTempOrder ? handleCancelOrderWithDeleteTemp : undefined}
				confirmAltText={hasTempOrder ? i18next.t('purchase.yes_delete_temp_order') : undefined}
				confirmAltColor="error"
			/>
			<ConfirmDialog
				open={isExcelNotFoundDialogOpen}
				onClose={null}
				onConfirm={() => setIsExcelNotFoundDialogOpen(false)}
				title={i18next.t('address.invalid_address')}
				message={i18next.t('purchase.invalid_ids_cannot_add') + '\n\n' + excelNotFoundIds.join('\n')}
				button1Text={null}
				button2Text={i18next.t('common.confirm')}
			/>
			{orderUploadData && (
				<OrderUploadDialog
					open={isOrderUploadDialogOpen}
					onClose={handleOrderUploadCancel}
					totalOrderCount={orderUploadData.normalOrders.length + orderUploadData.invalidOrders.length + orderUploadData.duplicateClassroomOrders.length + orderUploadData.duplicateProductOrders.length}
					normalOrders={orderUploadData.normalOrders || []}
					invalidOrders={orderUploadData.invalidOrders || []}
					duplicateClassroomOrders={orderUploadData.duplicateClassroomOrders || []}
					duplicateProductOrders={orderUploadData.duplicateProductOrders || []}
				/>
			)}
			{/* 중복 alias 선택 다이얼로그 */}
			<Dialog
				open={isDuplicateAliasDialogOpen}
				onClose={() => setIsDuplicateAliasDialogOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>{i18next.t('purchase.same_alias_multiple_addresses')}</DialogTitle>
				<DialogContent>
					{duplicateAliasRows.map((row, idx) => (
						<Box key={idx} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								{i18next.t('address.alias_label')}: {row.aliasName} ({i18next.t('common.row_number')}: {row.excelRow})
							</Typography>
							<Typography variant="body2" sx={{ mb: 1 }}>{i18next.t('purchase.which_address')}</Typography>
							<RadioGroup
								value={row.selectedId !== null ? String(row.selectedId) : ''}
								onChange={(e) => {
									setDuplicateAliasRows(prev => prev.map((r, i) =>
										i === idx ? { ...r, selectedId: Number(e.target.value) } : r
									));
								}}
							>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" />
											<TableCell sx={{ fontSize: '0.75rem', fontWeight: 800 }}>ID</TableCell>
											<TableCell sx={{ fontSize: '0.75rem', fontWeight: 800 }}>{i18next.t('user.username')}</TableCell>
											<TableCell sx={{ fontSize: '0.75rem', fontWeight: 800 }}>{i18next.t('order.classroom')}</TableCell>
											<TableCell sx={{ fontSize: '0.75rem', fontWeight: 800 }}>{i18next.t('address.address')}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{row.candidates.map(candidate => (
											<TableRow key={candidate.addressId}>
												<TableCell padding="checkbox">
													<FormControlLabel
														value={String(candidate.addressId)}
														control={<Radio size="small" />}
														label=""
														sx={{ m: 0 }}
													/>
												</TableCell>
												<TableCell sx={{ fontSize: '0.7rem' }}>{candidate.addressId}</TableCell>
												<TableCell sx={{ fontSize: '0.7rem' }}>{candidate.userName}</TableCell>
												<TableCell sx={{ fontSize: '0.7rem' }}>{candidate.classroomName}</TableCell>
												<TableCell sx={{ fontSize: '0.7rem' }}>{candidate.address}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</RadioGroup>
							{row.selectedId !== null && (
								<Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
									{i18next.t('purchase.specify_id_for_alias', { id: row.selectedId })}
								</Alert>
							)}
						</Box>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDuplicateAliasDialogOpen(false)}>{i18next.t('common.cancel')}</Button>
					<Button
						variant="contained"
						onClick={handleDuplicateAliasConfirm}
						disabled={duplicateAliasRows.some(r => r.selectedId === null)}
					>
						{i18next.t('common.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
			{/* ID와 alias/address 충돌 경고 다이얼로그 */}
			<ConfirmDialog
				open={isConflictWarningDialogOpen}
				onClose={null}
				onConfirm={() => setIsConflictWarningDialogOpen(false)}
				title={i18next.t('purchase.unmatched_items')}
				message={conflictWarnings.map(w =>
					i18next.t('purchase.excel_row_mismatch', {
						row: w.excelRow,
						id: w.id,
						field: w.field,
						excelValue: w.excelValue,
						actualValue: w.actualValue
					})
				).join('\n')}
				button1Text={null}
				button2Text={i18next.t('common.confirm')}
			/>
		</Container>
	);
};

export default DeliveryAddressList;